import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Download, Loader2, Lock, Star, MessageSquare, BadgeCheck, X, Check, Sparkles, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [fbLoading, setFbLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics")
      .then(({ data }) => setData(data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));

    api.get("/admin/feedback?limit=200")
      .then(({ data }) => setFeedback(data.items || []))
      .catch(() => {})
      .finally(() => setFbLoading(false));
  }, []);

  const exportFile = async (collection, format) => {
    try {
      const resp = await api.get(`/admin/export?collection=${collection}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${collection}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${collection}.${format}`);
    } catch { toast.error("Export failed"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  if (denied) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 text-center">
        <Lock size={36} className="mx-auto" />
        <h1 className="editorial-h2 mt-6">Admin only.</h1>
        <p className="text-sm text-muted-foreground mt-3">
          Add your email to <code>ADMIN_EMAILS</code> in <code>backend/.env</code> (comma-separated), or set <code>role: "admin"</code> on your user document, then refresh.
        </p>
        <p className="text-xs text-muted-foreground mt-2">Currently signed in: {user?.email}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <p className="eyebrow">Admin · Operations</p>
      <h1 className="editorial-h1 mt-3">Pulse of the atelier.</h1>

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Total users" value={data?.users?.total} />
        <Card label="Paid users" value={data?.users?.paid} sub={`${data?.users?.conversion_rate || 0}% conversion`} />
        <Card label="DAU / MAU" value={`${data?.active?.dau || 0} / ${data?.active?.mau || 0}`} />
        <Card label="Revenue · 30d" value={`₹${(data?.revenue_30d_inr || 0).toLocaleString()}`} />
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <section className="border border-border p-6">
          <p className="eyebrow">Revenue by plan · 30d</p>
          <table className="w-full mt-4 text-sm">
            <thead><tr className="border-b border-border">
              <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Plan</th>
              <th className="text-right p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Tx</th>
              <th className="text-right p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">₹</th>
            </tr></thead>
            <tbody>
              {(data?.revenue_by_plan || []).map((r) => (
                <tr key={r.plan_id} className="border-b border-border">
                  <td className="p-2">{r.plan_id}</td>
                  <td className="p-2 text-right">{r.count}</td>
                  <td className="p-2 text-right font-serif">{r.amount?.toLocaleString()}</td>
                </tr>
              ))}
              {(!data?.revenue_by_plan?.length) && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">No paid transactions yet.</td></tr>}
            </tbody>
          </table>
        </section>

        <section className="border border-border p-6">
          <p className="eyebrow">Feature usage · 30d</p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Card label="Outfits" value={data?.feature_usage_30d?.outfits || 0} />
            <Card label="Try-Ons" value={data?.feature_usage_30d?.tryons || 0} />
            <Card label="Posts" value={data?.feature_usage_30d?.posts || 0} />
          </div>
          <p className="eyebrow mt-6">Top actions</p>
          <ul className="mt-3 text-sm">
            {(data?.top_actions || []).map((a) => (
              <li key={a.type} className="flex justify-between border-b border-border py-1.5">
                <span className="text-muted-foreground">{a.type}</span>
                <span className="font-serif">{a.count}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="mt-10 border border-border p-6" data-testid="admin-feedback-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="eyebrow flex items-center gap-2">
              <MessageSquare size={12} /> User feedback
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Latest {feedback.length} submissions from Login / Home / Plans / Help.
            </p>
          </div>
          <div className="text-xs text-muted-foreground">
            {feedback.filter((f) => f.rating).length ? (
              <>
                Avg rating:{" "}
                <span className="font-serif text-base">
                  {(
                    feedback.filter((f) => f.rating).reduce((s, f) => s + f.rating, 0) /
                    feedback.filter((f) => f.rating).length
                  ).toFixed(1)}
                </span>{" "}
                / 5
              </>
            ) : (
              "No ratings yet"
            )}
          </div>
        </div>

        {fbLoading ? (
          <div className="py-8 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>
        ) : feedback.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No feedback yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-feedback-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Date</th>
                  <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Name</th>
                  <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Email</th>
                  <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Rating</th>
                  <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Context</th>
                  <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Message</th>
                </tr>
              </thead>
              <tbody>
                {feedback.map((f) => (
                  <tr key={f.feedback_id} className="border-b border-border align-top">
                    <td className="p-2 text-xs whitespace-nowrap">
                      {new Date(f.created_at).toLocaleString("en-IN", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </td>
                    <td className="p-2 text-xs">{f.name || "—"}</td>
                    <td className="p-2 text-xs">{f.email || "—"}</td>
                    <td className="p-2 text-xs whitespace-nowrap">
                      {f.rating ? (
                        <span className="inline-flex items-center gap-1">
                          <Star size={12} className="fill-accent text-accent" />
                          {f.rating}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-2 text-[10px] uppercase tracking-[0.18em]">{f.context || "other"}</td>
                    <td className="p-2 text-xs max-w-md">{f.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <StylistVerificationSection />

      <DeletionRequestsSection />

      <section className="mt-10 border border-border p-6">
        <p className="eyebrow">Raw data export</p>
        <p className="text-sm text-muted-foreground mt-2">
          Download up to 5,000 rows per collection. Use for audit, BI dashboards or migrations.
        </p>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {["users", "payment_transactions", "outfits", "user_actions", "community_posts"].map((c) => (
            <div key={c} className="border border-border p-3 flex items-center justify-between">
              <code className="text-xs">{c}</code>
              <div className="flex gap-2">
                <Button data-testid={`export-${c}-csv`} variant="outline" onClick={() => exportFile(c, "csv")}
                        className="rounded-none uppercase tracking-[0.18em] text-[10px] h-9">
                  <Download size={12} className="mr-1" /> CSV
                </Button>
                <Button data-testid={`export-${c}-xlsx`} variant="outline" onClick={() => exportFile(c, "xlsx")}
                        className="rounded-none uppercase tracking-[0.18em] text-[10px] h-9">
                  <Download size={12} className="mr-1" /> XLSX
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Card({ label, value, sub }) {
  return (
    <div className="border border-border p-6">
      <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <p className="font-serif text-4xl mt-2 tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

/**
 * Stylist verification queue — admin picks pending/approved/rejected filter,
 * sees applicant details + specialties, and approves / rejects / features
 * inline. Every action is a single PATCH call; the row updates in place.
 */
function StylistVerificationSection() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/stylists${filter === "all" ? "" : `?status=${filter}`}`)
      .then(({ data }) => setRows(data.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [filter]);   // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (row, action) => {
    setBusyId(row.id);
    try {
      if (action === "approve") {
        await api.patch(`/admin/stylists/${row.id}/verify`, { status: "approved" });
        toast.success(`Approved ${row.display_name}`);
      } else if (action === "reject") {
        const reason = window.prompt("Reason for rejection? (shown to stylist)") || "Not disclosed";
        await api.patch(`/admin/stylists/${row.id}/verify`, { status: "rejected", reason });
        toast.success(`Rejected ${row.display_name}`);
      } else if (action === "suspend") {
        const reason = window.prompt("Reason for suspension?") || "";
        await api.patch(`/admin/stylists/${row.id}/verify`, { status: "suspended", reason });
        toast.success(`Suspended ${row.display_name}`);
      } else if (action === "feature") {
        await api.patch(`/admin/stylists/${row.id}/feature`, { featured: !row.featured });
        toast.success(row.featured ? "Removed from featured" : "Added to featured");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusyId(null); }
  };

  const filters = [
    { k: "pending", label: "Pending", tid: "admin-stylists-filter-pending" },
    { k: "approved", label: "Approved", tid: "admin-stylists-filter-approved" },
    { k: "rejected", label: "Rejected", tid: "admin-stylists-filter-rejected" },
    { k: "suspended", label: "Suspended", tid: "admin-stylists-filter-suspended" },
    { k: "all", label: "All", tid: "admin-stylists-filter-all" },
  ];

  return (
    <section
      className="mt-10 border border-border p-6"
      data-testid="admin-stylists-section"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow flex items-center gap-2">
            <BadgeCheck size={12} /> Stylist verification queue
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Review applications and manage the featured roster.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.k}
              data-testid={f.tid}
              onClick={() => setFilter(f.k)}
              className={`text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 border transition-colors ${
                filter === f.k
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border hover:border-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8" data-testid="admin-stylists-empty">
          No {filter === "all" ? "" : filter} stylists.
        </p>
      ) : (
        <div className="mt-4 space-y-3" data-testid="admin-stylists-list">
          {rows.map((r) => (
            <div key={r.id} className="border border-border p-4" data-testid={`admin-stylist-row-${r.id}`}>
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-lg">{r.display_name}</p>
                    {r.verification_status === "approved" && <BadgeCheck size={14} className="text-accent" />}
                    {r.featured && <Sparkles size={12} className="text-accent" />}
                    <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border border-border text-muted-foreground">
                      {r.verification_status}
                    </span>
                  </div>
                  {r.headline && <p className="text-xs text-muted-foreground mt-1">{r.headline}</p>}
                  <p className="text-xs text-muted-foreground mt-2">
                    {r.applicant?.email} · {r.city} · {r.experience_years} yrs · {r.price_tier}
                  </p>
                  <p className="text-sm mt-3 line-clamp-2">{r.bio}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(r.specialties || []).slice(0, 6).map((s) => (
                      <span key={s} className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 border border-border text-muted-foreground">
                        {s}
                      </span>
                    ))}
                  </div>
                  {r.rejection_reason && (
                    <p className="text-xs text-destructive mt-2"><b>Reason:</b> {r.rejection_reason}</p>
                  )}
                </div>
                <div className="flex flex-col gap-2 min-w-[160px]">
                  {r.verification_status !== "approved" && (
                    <Button
                      data-testid={`admin-stylist-approve-${r.id}`}
                      onClick={() => act(r, "approve")}
                      disabled={busyId === r.id}
                      className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2"
                    >
                      <Check size={12} /> Approve
                    </Button>
                  )}
                  {r.verification_status === "pending" && (
                    <Button
                      data-testid={`admin-stylist-reject-${r.id}`}
                      onClick={() => act(r, "reject")}
                      disabled={busyId === r.id}
                      variant="outline"
                      className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2"
                    >
                      <X size={12} /> Reject
                    </Button>
                  )}
                  {r.verification_status === "approved" && (
                    <>
                      <Button
                        data-testid={`admin-stylist-feature-${r.id}`}
                        onClick={() => act(r, "feature")}
                        disabled={busyId === r.id}
                        variant="outline"
                        className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2"
                      >
                        <Sparkles size={12} /> {r.featured ? "Unfeature" : "Feature"}
                      </Button>
                      <Button
                        data-testid={`admin-stylist-suspend-${r.id}`}
                        onClick={() => act(r, "suspend")}
                        disabled={busyId === r.id}
                        variant="outline"
                        className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2"
                      >
                        <X size={12} /> Suspend
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


/**
 * Deletion requests queue — DPDPA §12 / Google Play "Data deletion" URL.
 * Admin sees the pending queue, verifies identity out-of-band (email),
 * marks as verified, then purges. Purge triggers a scoped hard-delete
 * of the target user's data across every owned collection.
 */
function DeletionRequestsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending_verification");
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/deletion-requests?limit=500")
      .then(({ data }) => setRows(data.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  const act = async (row, status) => {
    if (status === "purged" && !window.confirm(
      `Permanently purge every trace of ${row.email}? This CANNOT be undone.`,
    )) return;
    setBusyId(row.request_id);
    try {
      const note = status === "rejected"
        ? (window.prompt("Rejection reason?") || "Rejected by admin")
        : undefined;
      await api.patch(`/admin/deletion-requests/${row.request_id}`, { status, note });
      toast.success(status === "purged" ? "User purged." : `Marked ${status}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusyId(null); }
  };

  const filters = [
    { k: "pending_verification", label: "Pending", tid: "deletion-filter-pending" },
    { k: "verified", label: "Verified", tid: "deletion-filter-verified" },
    { k: "purged", label: "Purged", tid: "deletion-filter-purged" },
    { k: "rejected", label: "Rejected", tid: "deletion-filter-rejected" },
    { k: "all", label: "All", tid: "deletion-filter-all" },
  ];

  return (
    <section
      className="mt-10 border border-border p-6"
      data-testid="admin-deletion-section"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow flex items-center gap-2">
            <ShieldAlert size={12} /> Account deletion requests
          </p>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            DPDPA §12 & Google Play compliance. Verify identity by replying to the
            requester&apos;s email, then purge within 30 days.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.k}
              data-testid={f.tid}
              onClick={() => setFilter(f.k)}
              className={`text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 border transition-colors ${
                filter === f.k
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border hover:border-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8" data-testid="admin-deletion-empty">
          No {filter === "all" ? "" : filter.replace("_", " ")} requests.
        </p>
      ) : (
        <div className="mt-4 space-y-3" data-testid="admin-deletion-list">
          {filtered.map((r) => {
            const overdue = r.status === "pending_verification"
              && r.purge_by && new Date(r.purge_by) < new Date();
            return (
              <div
                key={r.request_id}
                className={`border p-4 ${overdue ? "border-destructive/60 bg-destructive/5" : "border-border"}`}
                data-testid={`admin-deletion-row-${r.request_id}`}
              >
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-mono text-sm">{r.email}</p>
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${
                        r.status === "purged"
                          ? "bg-muted text-muted-foreground border-border"
                          : r.status === "rejected"
                            ? "text-destructive border-destructive/40"
                            : r.status === "verified"
                              ? "text-accent border-accent/40"
                              : "border-border text-muted-foreground"
                      }`}>
                        {r.status.replace("_", " ")}
                      </span>
                      {overdue && (
                        <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border border-destructive text-destructive">
                          Overdue
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {r.user_name ? `${r.user_name} · ` : ""}
                      {r.user_id ? `uid ${r.user_id.slice(0, 12)}` : "Unregistered email"}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Requested {new Date(r.requested_at).toLocaleDateString()} · Purge by {new Date(r.purge_by).toLocaleDateString()}
                      {r.reviewed_at && ` · Reviewed ${new Date(r.reviewed_at).toLocaleDateString()}`}
                    </p>
                    {r.reason && <p className="text-sm mt-2 italic line-clamp-2">&ldquo;{r.reason}&rdquo;</p>}
                    {r.admin_note && <p className="text-xs text-muted-foreground mt-2"><b>Note:</b> {r.admin_note}</p>}
                  </div>
                  <div className="flex flex-col gap-2 min-w-[160px]">
                    {r.status === "pending_verification" && (
                      <>
                        <Button
                          data-testid={`admin-deletion-verify-${r.request_id}`}
                          onClick={() => act(r, "verified")}
                          disabled={busyId === r.request_id}
                          variant="outline"
                          className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2"
                        >
                          <Check size={12} /> Mark verified
                        </Button>
                        <Button
                          data-testid={`admin-deletion-reject-${r.request_id}`}
                          onClick={() => act(r, "rejected")}
                          disabled={busyId === r.request_id}
                          variant="outline"
                          className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2"
                        >
                          <X size={12} /> Reject
                        </Button>
                      </>
                    )}
                    {(r.status === "verified" || r.status === "pending_verification") && r.user_id && (
                      <Button
                        data-testid={`admin-deletion-purge-${r.request_id}`}
                        onClick={() => act(r, "purged")}
                        disabled={busyId === r.request_id}
                        className="rounded-none h-9 uppercase tracking-[0.18em] text-[10px] gap-2 bg-destructive hover:bg-destructive/90"
                      >
                        {busyId === r.request_id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Purge now
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
